"use client";

import Link from "next/link";

import { useTranslation } from "@/lib/hooks/use-translation";
import { blogHref } from "../data/paths";
import { tagPath } from "../data/tags";

type TagHeaderProps = {
  label: string;
  count: number;
  /** Every tag on the site — rendered as links so each tag page is crawlable. */
  allTags: { label: string; slug: string }[];
  activeSlug: string;
};

export function TagHeader({
  label,
  count,
  allTags,
  activeSlug,
}: TagHeaderProps) {
  const { t, language } = useTranslation();

  return (
    <header className="mx-auto flex max-w-3xl flex-col items-center gap-8 text-center">
      <div className="flex flex-col items-center gap-2">
        {/* No fade-up — it would hold back LCP. See blog-header.tsx. */}
        <h1 className="max-w-[680px] text-[26px] font-bold leading-[1.6] text-black min-[390px]:text-[28px] sm:text-[32px] sm:leading-[1.5] md:text-[40px] md:leading-[1.65]">
          {t("blogPage.tagPageHeading")}{" "}
          <span className="text-primary-strong">{label}</span>
        </h1>
        <p className="max-w-2xl text-sm font-medium leading-5 text-black/60">
          {t("blogPage.tagPageSubtitle")} · {t("blogPage.tagPageCount", { count })}
        </p>
      </div>

      {/* Tag switcher — the internal links that let crawlers reach every tag
          page from any other one, instead of only from article cards. */}
      <nav aria-label={t("blogPage.allTagsHeading")}>
        <ul className="flex flex-wrap items-center justify-center gap-2">
          {allTags.map((tag) => {
            const isActive = tag.slug === activeSlug;
            return (
              <li key={tag.slug}>
                <Link
                  href={blogHref(language, tagPath(tag.label))}
                  aria-current={isActive ? "page" : undefined}
                  className={
                    isActive
                      ? "inline-block rounded-full border border-primary-strong bg-primary-strong px-3 py-1 text-[11px] font-medium leading-4 text-white"
                      : "inline-block rounded-full border border-[#D3D5DB] bg-[#F3F4F6] px-3 py-1 text-[11px] font-medium leading-4 text-black/60 transition-colors hover:border-primary/40 hover:text-black/70"
                  }
                >
                  {tag.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <Link
        href={blogHref(language, "/blog")}
        className="text-sm font-medium text-black/60 underline underline-offset-4 transition-colors hover:text-black"
      >
        {t("blogPage.backToBlog")}
      </Link>
    </header>
  );
}
